import { Link } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../../styles/components/forms.css";

function displayProductName(sub) {
  if (sub?.product_id) return sub.product_id;
  if (sub?.name && !String(sub.name).includes("undefined")) return sub.name;
  try {
    const raw = typeof sub?.latest_receipt_data === "string"
      ? JSON.parse(sub.latest_receipt_data)
      : sub?.latest_receipt_data;
    const fromReceipt =
      raw?.transactionInfo?.productId ||
      raw?.transactionInfo?.product_id ||
      null;
    if (fromReceipt) return fromReceipt;
  } catch (_) {
    // ignore parse errors
  }
  return sub?.name || "—";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("nb-NO");
}

const thStyle = {
  padding: "0.65rem 0.75rem",
  borderBottom: "2px solid #e2e8f0",
  whiteSpace: "nowrap",
  verticalAlign: "bottom",
};

const tdStyle = {
  padding: "0.65rem 0.75rem",
  borderBottom: "1px solid #eee",
  verticalAlign: "middle",
};

export default function AppleSubscriptionsAdmin() {
  const [tab, setTab] = useState("orphans");
  const [orphans, setOrphans] = useState([]);
  const [insertions, setInsertions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");

  const [linkTarget, setLinkTarget] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [linking, setLinking] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const fetchOrphans = useCallback(async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/storekit/admin/orphan-subscriptions`,
      {
        withCredentials: true,
        params: { limit: 200, search: search.trim() || undefined },
      }
    );
    setOrphans(response.data?.subscriptions || []);
  }, [search]);

  const fetchInsertions = useCallback(async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/admin/apple-subscription-insertions`,
      {
        withCredentials: true,
        params: { limit: 200, search: search.trim() || undefined },
      }
    );
    setInsertions(response.data?.insertions || []);
  }, [search]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      if (tab === "orphans") {
        await fetchOrphans();
      } else {
        await fetchInsertions();
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Kunne ikke hente Apple-abonnementer."
      );
    } finally {
      setLoading(false);
    }
  }, [tab, fetchOrphans, fetchInsertions]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openLinkModal = async (sub) => {
    setLinkTarget(sub);
    setSelectedUserId(null);
    setUserSearch("");
    setMessage("");
    setUsersLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users`,
        { withCredentials: true }
      );
      setUsers(response.data?.users || response.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message || "Kunne ikke hente brukere for kobling."
      );
      setLinkTarget(null);
    } finally {
      setUsersLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users.slice(0, 50);
    return users
      .filter((u) => {
        const haystack = [u.name, u.email, String(u.idusers || u.id || "")]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 50);
  }, [users, userSearch]);

  const linkSubscription = async () => {
    if (!linkTarget || !selectedUserId) return;
    setLinking(true);
    setMessage("");
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/admin/link-ios-subscription`,
        {
          originalTransactionId: linkTarget.original_transaction_id,
          userId: selectedUserId,
        },
        { withCredentials: true }
      );
      setMessage("Abonnement koblet til bruker.");
      setLinkTarget(null);
      await fetchOrphans();
    } catch (err) {
      setMessage(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Kobling feilet."
      );
    } finally {
      setLinking(false);
    }
  };

  return (
    <div
      className="page-container"
      style={{ maxWidth: "min(1600px, 98vw)", width: "100%" }}
    >
      <div className="page-header-stack">
        <Link to="/dashboard" className="back-button">
          <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
        </Link>
        <h1>Apple-abonnementer (orphan)</h1>
        <p style={{ marginTop: 0, color: "#555" }}>
          Abonnementer uten brukerlink, og audit-logg for Apple-innsettinger.
        </p>
      </div>

      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
        <button
          type="button"
          className={tab === "orphans" ? "primary-button" : "secondary-button"}
          onClick={() => setTab("orphans")}
        >
          Orphans ({orphans.length})
        </button>
        <button
          type="button"
          className={tab === "log" ? "primary-button" : "secondary-button"}
          onClick={() => setTab("log")}
        >
          Purchase log
        </button>
        <button type="button" className="secondary-button" onClick={loadData}>
          Oppdater
        </button>
      </div>

      <div
        style={{
          marginBottom: "1rem",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "0.35rem",
          alignItems: "stretch",
        }}
      >
        <label htmlFor="appleSubSearch" style={{ fontWeight: 600 }}>
          Søk
        </label>
        <input
          id="appleSubSearch"
          className="search-input"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            tab === "orphans"
              ? "Produkt, transaction id, navn…"
              : "E-post, produkt, transaction id, source…"
          }
          style={{ width: "100%", boxSizing: "border-box" }}
        />
      </div>

      {message && (
        <p style={{ color: message.includes("feilet") ? "#b00020" : "#0a7a2f" }}>
          {message}
        </p>
      )}
      {error && <p style={{ color: "#b00020" }}>{error}</p>}
      {loading && <p>Laster…</p>}

      {!loading && tab === "orphans" && (
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            background: "#fff",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            padding: "0.75rem",
          }}
        >
          {orphans.length === 0 ? (
            <p>Ingen orphan Apple-abonnementer funnet.</p>
          ) : (
            <table
              style={{
                width: "100%",
                minWidth: 1100,
                borderCollapse: "collapse",
                tableLayout: "auto",
              }}
            >
              <thead>
                <tr>
                  <th align="left" style={thStyle}>ID</th>
                  <th align="left" style={thStyle}>Produkt</th>
                  <th align="left" style={thStyle}>Status</th>
                  <th align="left" style={thStyle}>Original TX</th>
                  <th align="left" style={thStyle}>Kjøpt</th>
                  <th align="left" style={thStyle}>Utløper</th>
                  <th align="left" style={thStyle}>Opprettet</th>
                  <th align="left" style={thStyle}>Handling</th>
                </tr>
              </thead>
              <tbody>
                {orphans.map((sub) => (
                  <tr key={sub.idsubscription}>
                    <td style={tdStyle}>{sub.idsubscription}</td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {displayProductName(sub)}
                    </td>
                    <td style={tdStyle}>{sub.status || "—"}</td>
                    <td
                      style={{
                        ...tdStyle,
                        fontFamily: "monospace",
                        fontSize: "0.85rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sub.original_transaction_id || "—"}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {formatDate(sub.purchase_date)}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {formatDate(sub.expiration_date || sub.current_period_end)}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {formatDate(sub.created_at)}
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        className="primary-button"
                        disabled={!sub.original_transaction_id}
                        onClick={() => openLinkModal(sub)}
                      >
                        Koble til bruker
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && tab === "log" && (
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            background: "#fff",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            padding: "0.75rem",
          }}
        >
          {insertions.length === 0 ? (
            <p>Ingen rader i purchase log ennå.</p>
          ) : (
            <table
              style={{
                width: "100%",
                minWidth: 1000,
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th align="left" style={thStyle}>ID</th>
                  <th align="left" style={thStyle}>Bruker</th>
                  <th align="left" style={thStyle}>Produkt</th>
                  <th align="left" style={thStyle}>Source</th>
                  <th align="left" style={thStyle}>Original TX</th>
                  <th align="left" style={thStyle}>Kjøpt</th>
                  <th align="left" style={thStyle}>Logget</th>
                </tr>
              </thead>
              <tbody>
                {insertions.map((row) => (
                  <tr key={row.id}>
                    <td style={tdStyle}>{row.id}</td>
                    <td style={tdStyle}>
                      {row.user_email || row.users_idusers || "—"}
                      {row.user_name ? ` (${row.user_name})` : ""}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {row.product_id || "—"}
                    </td>
                    <td style={tdStyle}>
                      {row.source}
                      {row.notification_type ? ` / ${row.notification_type}` : ""}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        fontFamily: "monospace",
                        fontSize: "0.85rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.original_transaction_id || "—"}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {formatDate(row.purchase_date)}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      {formatDate(row.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {linkTarget && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "1.25rem",
              width: "min(560px, 100%)",
              maxHeight: "85vh",
              overflow: "auto",
            }}
          >
            <h2 style={{ marginTop: 0 }}>Koble abonnement til bruker</h2>
            <p style={{ fontSize: "0.95rem" }}>
              Produkt: <strong>{displayProductName(linkTarget)}</strong>
              <br />
              Original TX:{" "}
              <code>{linkTarget.original_transaction_id}</code>
            </p>

            <label htmlFor="userSearchLink">Søk bruker</label>
            <input
              id="userSearchLink"
              className="search-input"
              type="search"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Navn eller e-post"
              style={{ width: "100%", marginBottom: "0.75rem" }}
            />

            {usersLoading ? (
              <p>Laster brukere…</p>
            ) : (
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  maxHeight: 260,
                  overflow: "auto",
                  marginBottom: "1rem",
                }}
              >
                {filteredUsers.map((u) => {
                  const id = u.idusers || u.id;
                  const selected = Number(selectedUserId) === Number(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setSelectedUserId(id)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "0.6rem 0.75rem",
                        border: "none",
                        borderBottom: "1px solid #eee",
                        background: selected ? "#e8f4ff" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <strong>{u.name || "Uten navn"}</strong>
                      <br />
                      <span style={{ color: "#555", fontSize: "0.9rem" }}>
                        {u.email} · id {id}
                      </span>
                    </button>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <p style={{ padding: "0.75rem" }}>Ingen brukere funnet.</p>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setLinkTarget(null)}
                disabled={linking}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={linkSubscription}
                disabled={!selectedUserId || linking}
              >
                {linking ? "Kobler…" : "Bekreft kobling"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
